import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import { API_BASE_URL, deleteRequest, extractErrorMessage, getBlobRequest, getRequest, postRequest, putRequest } from "../../../api";

import {
    DOWNLOAD_MODULE_CLE_TEMPLATE_FAIL,
    DOWNLOAD_MODULE_CLE_TEMPLATE_SUCCESS,
    FETCH_MODULES_CLE_REQUEST,
    IMPORT_MODULE_CLE_EXCEL_FAIL,
    IMPORT_MODULE_CLE_EXCEL_SUCCESS,
    fetchModulesCleSuccess,
    fetchModulesCleFail,
    createModuleCleSuccess,
    createModuleCleFail,
    updateModuleCleSuccess,
    updateModuleCleFail,
    deleteModuleCleSuccess,
    deleteModuleCleFail,
    setModulesCleBulkLoading,
    setModulesCleLoading,
} from "../actions/modulesCleActions";

const getUid = () => sessionStorage.getItem("uid");
const getToken = () => sessionStorage.getItem("token");

export function buildModuleCleFormData(payload) {
    const formData = new FormData();
    const imageFile = payload?.imageFile || null;
    const clean = { ...payload };

    delete clean.imageFile;

    formData.append("payload", JSON.stringify(clean));

    if (imageFile) {
        formData.append("image", imageFile);
    }

    return formData;
}

function downloadBlobAsFile(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export function* fetchModulesCleSaga() {
    const url = `${API_BASE_URL}/api/modules-cle`;
    yield put(setModulesCleLoading(true));
    try {
        const resp = yield call(getRequest, { url, uid: getUid(), token: getToken() });
        yield put(fetchModulesCleSuccess(resp.data));
    } catch (e) {
        yield put(fetchModulesCleFail(e.response?.data?.message || e.message));
    } finally {
        yield put(setModulesCleLoading(false));
    }
}

export function* createModuleCleSaga(action) {
    const url = `${API_BASE_URL}/api/modules-cle`;
    yield put(setModulesCleLoading(true));
    try {
        const data = buildModuleCleFormData(action.payload);
        const resp = yield call(postRequest, { url, data, uid: getUid(), token: getToken() });
        yield put(createModuleCleSuccess(resp.data));
        yield call([Swal, Swal.fire], { icon: "success", text: "Module ajouté", timer: 1400, showConfirmButton: false });
        yield put({ type: FETCH_MODULES_CLE_REQUEST });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur ajout module";
        yield put(createModuleCleFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", text: msg, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setModulesCleLoading(false));
    }
}

export function* updateModuleCleSaga(action) {
    const { id, payload } = action.payload;
    const url = `${API_BASE_URL}/api/modules-cle/${id}`;
    yield put(setModulesCleLoading(true));
    try {
        const data = buildModuleCleFormData(payload);
        const resp = yield call(putRequest, { url, data, uid: getUid(), token: getToken() });
        yield put(updateModuleCleSuccess(resp.data));
        yield call([Swal, Swal.fire], { icon: "success", text: "Module modifié", timer: 1400, showConfirmButton: false });
        yield put({ type: FETCH_MODULES_CLE_REQUEST });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur modification module";
        yield put(updateModuleCleFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", text: msg, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setModulesCleLoading(false));
    }
}

export function* deleteModuleCleSaga(action) {
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/modules-cle/${id}`;
    yield put(setModulesCleLoading(true));
    try {
        yield call(deleteRequest, { url, uid: getUid(), token: getToken() });
        yield put(deleteModuleCleSuccess(id));
        yield call([Swal, Swal.fire], { icon: "success", text: "Module supprime", timer: 1400, showConfirmButton: false });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur suppression module";
        yield put(deleteModuleCleFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", text: msg, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setModulesCleLoading(false));
    }
}

export function* downloadModuleCleTemplateSaga() {
    const url = `${API_BASE_URL}/api/modules-cle/import/template`;
    yield put(setModulesCleBulkLoading(true));
    try {
        const response = yield call(getBlobRequest, {
            url,
            token: getToken(),
            extraHeaders: { uid: getUid() },
        });
        downloadBlobAsFile(new Blob([response.data]), "template_modules_cles.xlsx");
        yield put({ type: DOWNLOAD_MODULE_CLE_TEMPLATE_SUCCESS });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur telechargement template modules");
        yield put({ type: DOWNLOAD_MODULE_CLE_TEMPLATE_FAIL, payload: message });
        yield call([Swal, Swal.fire], { icon: "error", text: message, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setModulesCleBulkLoading(false));
    }
}

export function* importModuleCleExcelSaga(action) {
    const { file, dryRun, action: importAction } = action.payload;
    const url = `${API_BASE_URL}/api/modules-cle/import?dryRun=${dryRun ? "true" : "false"}&action=${importAction}`;
    yield put(setModulesCleBulkLoading(true));
    try {
        const formData = new FormData();
        formData.append("file", file);

        const response = yield call(postRequest, { url, data: formData, uid: getUid(), token: getToken() });
        yield put({ type: IMPORT_MODULE_CLE_EXCEL_SUCCESS, payload: response.data });

        if (!dryRun) {
            yield call([Swal, Swal.fire], {
                icon: "success",
                text: `${response.data.createdCount || 0} creation(s), ${response.data.updatedCount || 0} modification(s), ${response.data.skippedCount || 0} ligne(s) ignoree(s)`,
                timer: 2400,
                showConfirmButton: false,
            });
            yield put({ type: FETCH_MODULES_CLE_REQUEST });
        } else {
            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${response.data.preview?.length || 0} ligne(s) valide(s), ${response.data.errorCount || 0} erreur(s)`,
                timer: 2200,
                showConfirmButton: false,
            });
        }
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur import modules Excel");
        yield put({ type: IMPORT_MODULE_CLE_EXCEL_FAIL, payload: message });
        yield call([Swal, Swal.fire], { icon: "error", text: message, timer: 2400, showConfirmButton: false });
    } finally {
        yield put(setModulesCleBulkLoading(false));
    }
}
